<script setup lang="ts">
import { ref, computed } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { flame, albums, heart, chatbubbles, locate } from 'ionicons/icons';
import { useI18n } from '@/i18n';

const emit = defineEmits<{ done: [] }>();
const { t } = useI18n();

interface Slide {
  icon: string;
  gradient: string;
  titleKey: string;
  descKey: string;
}

const slides: Slide[] = [
  {
    icon: flame,
    gradient: 'linear-gradient(135deg, #EF4444, #F97316)',
    titleKey: 'onboarding_slide1_title',
    descKey: 'onboarding_slide1_desc',
  },
  {
    icon: albums,
    gradient: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
    titleKey: 'onboarding_slide2_title',
    descKey: 'onboarding_slide2_desc',
  },
  {
    icon: heart,
    gradient: 'linear-gradient(135deg, #EC4899, #F43F5E)',
    titleKey: 'onboarding_slide3_title',
    descKey: 'onboarding_slide3_desc',
  },
  {
    icon: locate,
    gradient: 'linear-gradient(135deg, #10B981, #34D399)',
    titleKey: 'onboarding_slide5_title',
    descKey: 'onboarding_slide5_desc',
  },
  {
    icon: chatbubbles,
    gradient: 'linear-gradient(135deg, #3B82F6, #06B6D4)',
    titleKey: 'onboarding_slide4_title',
    descKey: 'onboarding_slide4_desc',
  },
];

const current = ref(0);
const isLast = computed(() => current.value === slides.length - 1);

function goTo(i: number) {
  current.value = i;
}

function next() {
  if (isLast.value) {
    finish();
  } else {
    current.value++;
  }
}

function finish() {
  localStorage.setItem('swipewish.onboarding_done', '1');
  emit('done');
}
</script>

<template>
  <Teleport to="body">
    <div class="ob-overlay" role="dialog" aria-modal="true" aria-label="Welcome tutorial">
      <!-- Ambient blobs -->
      <div class="ob-blob ob-blob--1" aria-hidden="true" />
      <div class="ob-blob ob-blob--2" aria-hidden="true" />

      <!-- Top bar: logo + skip -->
      <div class="ob-topbar">
        <img src="/logo.svg" alt="SwipeWish" class="ob-logo" />
        <button class="ob-skip" @click="finish">{{ t('onboarding_skip') }}</button>
      </div>

      <!-- Slide content -->
      <div class="ob-stage">
        <transition name="ob-slide" mode="out-in">
          <div :key="current" class="ob-slide">
            <div class="ob-icon-bubble" :style="{ background: slides[current].gradient }">
              <ion-icon :icon="slides[current].icon" class="ob-icon" />
            </div>
            <h2 class="ob-title">{{ t(slides[current].titleKey) }}</h2>
            <p class="ob-desc">{{ t(slides[current].descKey) }}</p>
          </div>
        </transition>
      </div>

      <!-- Footer: dots + button -->
      <div class="ob-footer">
        <div class="ob-dots" role="tablist">
          <button
            v-for="(_, i) in slides"
            :key="i"
            class="ob-dot"
            :class="{ 'ob-dot--active': i === current }"
            @click="goTo(i)"
            :aria-label="`Slide ${i + 1} of ${slides.length}`"
            :aria-selected="i === current"
            role="tab"
          />
        </div>
        <ion-button
          expand="block"
          fill="solid"
          color="primary"
          class="ob-cta"
          @click="next"
        >
          {{ isLast ? t('onboarding_start') : t('onboarding_next') }}
        </ion-button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ob-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: var(--bg-mystic);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overflow: hidden;
}

/* ── Ambient blobs ──────────────────────────────────────────────────────── */
.ob-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: ob-blob-drift 14s ease-in-out infinite alternate;
}
.ob-blob--1 {
  width: 340px; height: 340px;
  background: radial-gradient(circle, rgba(124,58,237,0.22) 0%, transparent 70%);
  top: -100px; left: -80px;
}
.ob-blob--2 {
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(167,139,250,0.14) 0%, transparent 70%);
  bottom: 100px; right: -60px;
  animation-delay: -7s;
}
@keyframes ob-blob-drift {
  from { transform: translate(0, 0) scale(1); }
  to   { transform: translate(24px, 32px) scale(1.1); }
}

/* ── Top bar ────────────────────────────────────────────────────────────── */
.ob-topbar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 0;
  position: relative;
  z-index: 1;
}
.ob-logo {
  width: 36px;
  height: auto;
  opacity: 0.9;
}
.ob-skip {
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 4px;
  border-radius: 8px;
  transition: color var(--md-duration-short4) var(--md-easing-standard);
  -webkit-tap-highlight-color: transparent;
}
.ob-skip:hover { color: var(--text-main); }

/* ── Slide stage ────────────────────────────────────────────────────────── */
.ob-stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 32px;
  position: relative;
  z-index: 1;
}

.ob-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 28px;
  max-width: 340px;
  width: 100%;
}

/* ── Icon bubble ────────────────────────────────────────────────────────── */
.ob-icon-bubble {
  width: 128px;
  height: 128px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 48px rgba(0,0,0,0.35);
  flex-shrink: 0;
}
.ob-icon {
  font-size: 54px;
  color: white;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.25));
}

/* ── Text ───────────────────────────────────────────────────────────────── */
.ob-title {
  font-size: 26px;
  font-weight: 800;
  color: var(--text-main);
  margin: 0;
  letter-spacing: -0.4px;
  line-height: 1.2;
}
.ob-desc {
  font-size: 15px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.65;
}

/* ── Footer ─────────────────────────────────────────────────────────────── */
.ob-footer {
  width: 100%;
  padding: 20px 24px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  position: relative;
  z-index: 1;
}

/* ── Dot indicators ─────────────────────────────────────────────────────── */
.ob-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}
.ob-dot {
  height: 8px;
  border-radius: 4px;
  width: 8px;
  background: rgba(255,255,255,0.2);
  border: none;
  cursor: pointer;
  padding: 0;
  transition:
    width var(--md-duration-medium2) var(--md-easing-emphasized-decelerate),
    background var(--md-duration-short4) var(--md-easing-standard);
  -webkit-tap-highlight-color: transparent;
}
.ob-dot--active {
  width: 28px;
  background: var(--neon-purple);
}

/* ── CTA button ─────────────────────────────────────────────────────────── */
.ob-cta {
  max-width: 340px;
  --border-radius: 18px;
  --padding-top: 17px;
  --padding-bottom: 17px;
  font-size: 16px;
  font-weight: 700;
}

/* ── Slide transition ───────────────────────────────────────────────────── */
.ob-slide-enter-active {
  transition: all var(--md-duration-medium2) var(--md-easing-emphasized-decelerate);
}
.ob-slide-leave-active {
  transition: all var(--md-duration-medium1) var(--md-easing-emphasized-accelerate);
}
.ob-slide-enter-from {
  opacity: 0;
  transform: translateX(32px) scale(0.97);
}
.ob-slide-leave-to {
  opacity: 0;
  transform: translateX(-32px) scale(0.97);
}
</style>
